import { createContext, useContext, useState, useCallback, useEffect } from 'react'
import { useAuth } from './AuthContext'
import { displayPreferencesApi } from '../services/api'

const DEFAULTS = {
  showConfidenceScore: true,
  showAiBadge: true,
  showColorCoding: true,
}

const DisplayPreferencesContext = createContext(DEFAULTS)

export function DisplayPreferencesProvider({ children }) {
  const { isAuthenticated, accessToken } = useAuth()
  const [prefs, setPrefs] = useState(DEFAULTS)
  const [loading, setLoading] = useState(false)

  // Fetch display preferences from backend when authenticated
  useEffect(() => {
    if (!isAuthenticated || !accessToken) return

    setLoading(true)
    displayPreferencesApi.get()
      .then(res => {
        const data = res.data
        setPrefs({
          showConfidenceScore: data.show_confidence_score ?? true,
          showAiBadge: data.show_ai_badge ?? true,
          showColorCoding: data.show_color_coding ?? true,
        })
      })
      .catch(err => {
        console.warn('Failed to load display preferences, using defaults:', err)
      })
      .finally(() => setLoading(false))
  }, [isAuthenticated, accessToken])

  const updatePreferences = useCallback(async (updates) => {
    // Map frontend keys to backend keys
    const payload = {}
    if ('showConfidenceScore' in updates) payload.show_confidence_score = updates.showConfidenceScore
    if ('showAiBadge' in updates) payload.show_ai_badge = updates.showAiBadge
    if ('showColorCoding' in updates) payload.show_color_coding = updates.showColorCoding

    const res = await displayPreferencesApi.update(payload)
    const data = res.data
    setPrefs({
      showConfidenceScore: data.show_confidence_score ?? true,
      showAiBadge: data.show_ai_badge ?? true,
      showColorCoding: data.show_color_coding ?? true,
    })
    return data
  }, [])

  return (
    <DisplayPreferencesContext.Provider value={{ ...prefs, updatePreferences, loading }}>
      {children}
    </DisplayPreferencesContext.Provider>
  )
}

export function useDisplayPreferences() {
  return useContext(DisplayPreferencesContext)
}
